import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Textarea } from './ui/textarea';
import { Plus, CreditCard, Trash2, Filter, Edit, Tag, Search, Calendar } from 'lucide-react';
import { expenses as expensesAPI, expenseCategories as categoriesAPI } from '../api';
import { useToast } from '../hooks/use-toast';
import { formatMoney } from '../utils/currency';

const defaultCategories = [
  'Office Supplies',
  'Software',
  'Marketing',
  'Travel',
  'Utilities',
  'Rent',
  'Salaries',
  'Equipment',
  'Insurance',
  'Other'
];

const Expenses = ({ businessId, businessName, businessCurrency = 'USD', onUpdate }) => {
  const fmt = (amt) => formatMoney(amt, businessCurrency);
  const { toast } = useToast();
  const [expenses, setExpenses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isCategoryDialogOpen, setIsCategoryDialogOpen] = useState(false);
  const [editingExpense, setEditingExpense] = useState(null);
  const [filterCategory, setFilterCategory] = useState('all');
  const [customCategories, setCustomCategories] = useState([]);
  const [newCategoryName, setNewCategoryName] = useState('');
  
  // Search and filter state
  const [searchTerm, setSearchTerm] = useState('');
  const [dateFilter, setDateFilter] = useState('all');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [sortBy, setSortBy] = useState('date'); // date | created_at | amount | category
  
  const [formData, setFormData] = useState({
    category: '',
    amount: '',
    description: '',
    date: new Date().toISOString().split('T')[0],
    vendor: '',
    status: 'paid',
    due_date: ''
  });

  // Combined categories (default + custom)
  const allCategories = [...defaultCategories, ...customCategories];

  // Filter expenses based on search, category, vendor, and date
  const filteredExpenses = expenses.filter(expense => {
    // Search filter (description and vendor)
    const searchLower = searchTerm.toLowerCase();
    const matchesSearch = !searchTerm || 
      expense.description?.toLowerCase().includes(searchLower) ||
      expense.vendor?.toLowerCase().includes(searchLower);
    
    // Category filter
    const matchesCategory = filterCategory === 'all' || expense.category === filterCategory;
    
    // Date filter
    let matchesDate = true;
    if (dateFilter !== 'all') {
      const expenseDate = new Date(expense.date);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      
      if (dateFilter === 'today') {
        const expenseDateOnly = new Date(expenseDate);
        expenseDateOnly.setHours(0, 0, 0, 0);
        matchesDate = expenseDateOnly.getTime() === today.getTime();
      } else if (dateFilter === 'week') {
        const weekAgo = new Date(today);
        weekAgo.setDate(weekAgo.getDate() - 7);
        matchesDate = expenseDate >= weekAgo;
      } else if (dateFilter === 'month') {
        const monthAgo = new Date(today);
        monthAgo.setMonth(monthAgo.getMonth() - 1);
        matchesDate = expenseDate >= monthAgo;
      } else if (dateFilter === 'year') {
        const yearAgo = new Date(today);
        yearAgo.setFullYear(yearAgo.getFullYear() - 1);
        matchesDate = expenseDate >= yearAgo;
      } else if (dateFilter === 'custom' && startDate && endDate) {
        const start = new Date(startDate);
        const end = new Date(endDate);
        end.setHours(23, 59, 59, 999);
        matchesDate = expenseDate >= start && expenseDate <= end;
      }
    }
    
    return matchesSearch && matchesCategory && matchesDate;
  }).sort((a, b) => {
    if (sortBy === 'amount') return (b.amount || 0) - (a.amount || 0);
    if (sortBy === 'category') return (a.category || '').localeCompare(b.category || '');
    if (sortBy === 'created_at') return (b.created_at || '').localeCompare(a.created_at || '');
    // default: date desc with created_at tiebreaker
    const cmp = (b.date || '').localeCompare(a.date || '');
    if (cmp !== 0) return cmp;
    return (b.created_at || '').localeCompare(a.created_at || '');
  });

  useEffect(() => {
    fetchExpenses();
    loadCustomCategories();
  }, [businessId]);

  const loadCustomCategories = async () => {
    try {
      const response = await categoriesAPI.get(businessId);
      const dbCategories = response.data?.categories || [];
      // Also migrate any existing localStorage categories
      const localKey = `turbobooks_categories_${businessId}`;
      const localSaved = localStorage.getItem(localKey);
      if (localSaved) {
        const localCats = JSON.parse(localSaved);
        const merged = [...new Set([...dbCategories, ...localCats])];
        if (merged.length > dbCategories.length) {
          await categoriesAPI.update(businessId, merged);
          setCustomCategories(merged);
        } else {
          setCustomCategories(dbCategories);
        }
        localStorage.removeItem(localKey);
      } else {
        setCustomCategories(dbCategories);
      }
    } catch (error) {
      // Fallback to localStorage if API fails
      const saved = localStorage.getItem(`turbobooks_categories_${businessId}`);
      if (saved) setCustomCategories(JSON.parse(saved));
    }
  };

  const saveCustomCategory = async () => {
    if (!newCategoryName.trim()) return;
    
    const trimmedName = newCategoryName.trim();
    if (allCategories.includes(trimmedName)) {
      toast({
        title: 'Error',
        description: 'This category already exists',
        variant: 'destructive'
      });
      return;
    }
    
    const updated = [...customCategories, trimmedName];
    setCustomCategories(updated);
    setNewCategoryName('');
    setIsCategoryDialogOpen(false);
    try {
      await categoriesAPI.update(businessId, updated);
    } catch (error) {
      localStorage.setItem(`turbobooks_categories_${businessId}`, JSON.stringify(updated));
    }
    toast({
      title: 'Category Added',
      description: `"${trimmedName}" has been added to your categories`,
    });
  };

  const deleteCustomCategory = async (category) => {
    const updated = customCategories.filter(c => c !== category);
    setCustomCategories(updated);
    try {
      await categoriesAPI.update(businessId, updated);
    } catch (error) {
      localStorage.setItem(`turbobooks_categories_${businessId}`, JSON.stringify(updated));
    }
    toast({
      title: 'Category Removed',
      description: `"${category}" has been removed`,
    });
  };

  const fetchExpenses = async () => {
    try {
      setLoading(true);
      const response = await expensesAPI.getAll(businessId);
      setExpenses(response.data);
    } catch (error) {
      console.error('Error fetching expenses:', error);
      toast({
        title: 'Error',
        description: 'Failed to load expenses',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const resetForm = () => {
    setFormData({
      category: '',
      amount: '',
      description: '',
      date: new Date().toISOString().split('T')[0],
      vendor: '',
      status: 'paid',
      due_date: ''
    });
  };

  const buildPayload = () => ({
    ...formData,
    amount: parseFloat(formData.amount),
    status: formData.status || 'paid',
    due_date: formData.status === 'unpaid' ? (formData.due_date || null) : null,
  });

  const handleMarkPaid = async (expense) => {
    try {
      await expensesAPI.update(expense.id, { status: 'paid' });
      await fetchExpenses();
      if (onUpdate) onUpdate();
      toast({ title: 'Bill Paid', description: 'Expense marked as paid.' });
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to update expense', variant: 'destructive' });
    }
  };

  const renderPaymentStatusFields = (idPrefix) => (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
      <div>
        <Label htmlFor={`${idPrefix}status`}>Payment Status</Label>
        <Select value={formData.status || 'paid'} onValueChange={(v) => setFormData(prev => ({ ...prev, status: v }))}>
          <SelectTrigger id={`${idPrefix}status`} data-testid={`${idPrefix}expense-status-select`} className="min-h-[44px]">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="paid">Paid</SelectItem>
            <SelectItem value="unpaid">Unpaid (bill owed)</SelectItem>
          </SelectContent>
        </Select>
      </div>
      {formData.status === 'unpaid' && (
        <div>
          <Label htmlFor={`${idPrefix}due_date`}>Due Date</Label>
          <Input
            id={`${idPrefix}due_date`}
            name="due_date"
            type="date"
            value={formData.due_date || ''}
            onChange={handleInputChange}
            className="min-h-[44px]"
            data-testid={`${idPrefix}expense-due-date`}
          />
        </div>
      )}
    </div>
  );

  const handleCreateExpense = async (e) => {
    e.preventDefault();
    try {
      await expensesAPI.create({
        business_id: businessId,
        ...buildPayload()
      });
      setIsDialogOpen(false);
      resetForm();
      await fetchExpenses();
      if (onUpdate) onUpdate();
      toast({
        title: 'Expense Added',
        description: 'Expense has been recorded successfully.',
      });
    } catch (error) {
      const errorMessage = error.response?.data?.detail || 'Failed to create expense';
      toast({
        title: 'Error',
        description: errorMessage,
        variant: 'destructive'
      });
    }
  };

  const handleEditClick = (expense) => {
    setEditingExpense(expense);
    setFormData({
      category: expense.category || '',
      amount: expense.amount?.toString() || '',
      description: expense.description || '',
      date: expense.date || new Date().toISOString().split('T')[0],
      vendor: expense.vendor || '',
      status: expense.status || 'paid',
      due_date: expense.due_date || ''
    });
    setIsEditDialogOpen(true);
  };

  const handleUpdateExpense = async (e) => {
    e.preventDefault();
    try {
      await expensesAPI.update(editingExpense.id, buildPayload());
      setIsEditDialogOpen(false);
      setEditingExpense(null);
      resetForm();
      await fetchExpenses();
      if (onUpdate) onUpdate();
      toast({
        title: 'Expense Updated',
        description: 'Expense has been updated successfully.',
      });
    } catch (error) {
      const errorMessage = error.response?.data?.detail || 'Failed to update expense';
      toast({
        title: 'Error',
        description: errorMessage,
        variant: 'destructive'
      });
    }
  };

  const handleDeleteExpense = async (id) => {
    try {
      await expensesAPI.delete(id);
      await fetchExpenses();
      if (onUpdate) onUpdate();
      toast({
        title: 'Expense Deleted',
        description: 'Expense has been deleted successfully.',
      });
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to delete expense',
        variant: 'destructive'
      });
    }
  };

  const totalExpenses = filteredExpenses.reduce((sum, exp) => sum + exp.amount, 0);

  const getCategoryColor = (category) => {
    const colors = {
      'Office Supplies': 'bg-blue-100 text-blue-800',
      'Software': 'bg-purple-100 text-purple-800',
      'Marketing': 'bg-green-100 text-green-800',
      'Travel': 'bg-yellow-100 text-yellow-800',
      'Utilities': 'bg-red-100 text-red-800',
      'Rent': 'bg-indigo-100 text-indigo-800',
      'Salaries': 'bg-pink-100 text-pink-800',
      'Equipment': 'bg-orange-100 text-orange-800',
      'Insurance': 'bg-teal-100 text-teal-800',
      'Other': 'bg-gray-100 text-gray-800'
    };
    return colors[category] || 'bg-cyan-100 text-cyan-800'; // Custom categories get cyan
  };

  if (loading) {
    return (
      <div className="text-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-4 border-blue-600 mx-auto mb-4"></div>
        <p className="text-gray-600">Loading expenses...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <Card className="shadow-md">
        <CardHeader className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <CardTitle className="text-lg sm:text-xl font-bold text-gray-800">Expenses - {businessName}</CardTitle>
            <p className="text-sm text-gray-600 mt-1">
              Total: <span className="font-semibold text-red-600">{formatMoney(totalExpenses, businessCurrency)}</span>
            </p>
          </div>
          <div className="flex gap-2 flex-wrap">
            {/* Category Filter */}
            <Select value={filterCategory} onValueChange={setFilterCategory}>
              <SelectTrigger className="w-full sm:w-48" data-testid="expense-category-filter">
                <Filter className="h-4 w-4 mr-2" />
                <SelectValue placeholder="Filter by category" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Categories</SelectItem>
                {allCategories.map(category => (
                  <SelectItem key={category} value={category}>{category}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            {/* Manage Categories Button */}
            <Dialog open={isCategoryDialogOpen} onOpenChange={setIsCategoryDialogOpen}>
              <DialogTrigger asChild>
                <Button variant="outline" className="gap-2 min-h-[44px]" data-testid="manage-categories-btn">
                  <Tag className="h-4 w-4" />
                  <span className="hidden sm:inline">Categories</span>
                  <span className="sm:hidden">Manage</span>
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-md w-[95vw] sm:w-full">
                <DialogHeader>
                  <DialogTitle>Manage Expense Categories</DialogTitle>
                </DialogHeader>
                <div className="space-y-4">
                  {/* Add New Category */}
                  <div className="flex gap-2">
                    <Input
                      placeholder="New category name"
                      value={newCategoryName}
                      onChange={(e) => setNewCategoryName(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && saveCustomCategory()}
                      className="min-h-[44px]"
                      data-testid="new-category-input"
                    />
                    <Button onClick={saveCustomCategory} className="bg-blue-600 hover:bg-blue-700 min-h-[44px] min-w-[44px]" data-testid="add-category-btn">
                      <Plus className="h-4 w-4" />
                    </Button>
                  </div>

                  {/* Default Categories */}
                  <div>
                    <Label className="text-sm font-medium text-gray-500">Default Categories</Label>
                    <div className="mt-2 flex flex-wrap gap-2">
                      {defaultCategories.map(cat => (
                        <span key={cat} className={`px-3 py-1.5 rounded-full text-xs font-medium ${getCategoryColor(cat)}`}>
                          {cat}
                        </span>
                      ))}
                    </div>
                  </div>

                  {/* Custom Categories */}
                  {customCategories.length > 0 && (
                    <div>
                      <Label className="text-sm font-medium text-gray-500">Custom Categories</Label>
                      <div className="mt-2 space-y-2">
                        {customCategories.map(cat => (
                          <div key={cat} className="flex items-center justify-between bg-cyan-50 px-3 py-2.5 rounded-lg" data-testid={`custom-category-${cat}`}>
                            <span className="text-sm font-medium text-cyan-800">{cat}</span>
                            <Button
                              size="sm"
                              variant="ghost"
                              className="text-red-600 hover:bg-red-50 h-8 w-8 p-0 min-h-[32px]"
                              onClick={() => deleteCustomCategory(cat)}
                              data-testid={`delete-category-${cat}`}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </DialogContent>
            </Dialog>

            {/* Add Expense Button */}
            <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
              <DialogTrigger asChild>
                <Button className="bg-red-600 hover:bg-red-700 gap-2 min-h-[44px]" data-testid="add-expense-btn">
                  <Plus className="h-4 w-4" />
                  Add Expense
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-2xl">
                <DialogHeader>
                  <DialogTitle>Add New Expense</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleCreateExpense} className="space-y-4">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="category">Category *</Label>
                      <Select name="category" value={formData.category} onValueChange={(value) => setFormData(prev => ({ ...prev, category: value }))}>
                        <SelectTrigger data-testid="expense-category-select" className="min-h-[44px]">
                          <SelectValue placeholder="Select category" />
                        </SelectTrigger>
                        <SelectContent>
                          {allCategories.map(category => (
                            <SelectItem key={category} value={category}>{category}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label htmlFor="amount">Amount ($) *</Label>
                      <Input
                        id="amount"
                        name="amount"
                        type="number"
                        step="0.01"
                        value={formData.amount}
                        onChange={handleInputChange}
                        required
                        placeholder="0.00"
                        className="min-h-[44px]"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="date">Date *</Label>
                      <Input
                        id="date"
                        name="date"
                        type="date"
                        value={formData.date}
                        onChange={handleInputChange}
                        required
                        className="min-h-[44px]"
                      />
                    </div>
                    <div>
                      <Label htmlFor="vendor">Vendor</Label>
                      <Input
                        id="vendor"
                        name="vendor"
                        value={formData.vendor}
                        onChange={handleInputChange}
                        placeholder="Enter vendor name"
                        className="min-h-[44px]"
                      />
                    </div>
                  </div>
                  {renderPaymentStatusFields("")}
                  <div>
                    <Label htmlFor="description">Description *</Label>
                    <Textarea
                      id="description"
                      name="description"
                      value={formData.description}
                      onChange={handleInputChange}
                      required
                      placeholder="Enter expense description"
                      rows={3}
                    />
                  </div>
                  <div className="flex justify-end gap-2">
                    <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>
                      Cancel
                    </Button>
                    <Button type="submit" className="bg-red-600 hover:bg-red-700">
                      Add Expense
                    </Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
          </div>
        </CardHeader>
        <CardContent>
          {/* Search and Filter Bar */}
          <div className="mb-6 space-y-4">
            <div className="flex flex-wrap gap-4">
              {/* Search by vendor/description */}
              <div className="relative flex-1 min-w-[200px]">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  type="text"
                  placeholder="Search by vendor or description..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                  data-testid="expense-search"
                />
              </div>
              
              {/* Date Filter */}
              <Select value={dateFilter} onValueChange={setDateFilter}>
                <SelectTrigger className="w-[150px]" data-testid="expense-date-filter">
                  <Calendar className="h-4 w-4 mr-2" />
                  <SelectValue placeholder="Date" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Time</SelectItem>
                  <SelectItem value="today">Today</SelectItem>
                  <SelectItem value="week">Last 7 Days</SelectItem>
                  <SelectItem value="month">Last 30 Days</SelectItem>
                  <SelectItem value="year">Last Year</SelectItem>
                  <SelectItem value="custom">Custom Range</SelectItem>
                </SelectContent>
              </Select>

              <Select value={sortBy} onValueChange={setSortBy}>
                <SelectTrigger className="w-[170px]" data-testid="expense-sort-by">
                  <SelectValue placeholder="Sort by" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="date">Newest Expense Date</SelectItem>
                  <SelectItem value="created_at">Newest Created</SelectItem>
                  <SelectItem value="amount">Highest Amount</SelectItem>
                  <SelectItem value="category">Category</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            {/* Custom Date Range */}
            {dateFilter === 'custom' && (
              <div className="flex items-center gap-4 pl-4 border-l-2 border-blue-200">
                <div className="flex items-center gap-2">
                  <Label className="text-sm text-gray-600">From:</Label>
                  <Input
                    type="date"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    className="w-40"
                  />
                </div>
                <div className="flex items-center gap-2">
                  <Label className="text-sm text-gray-600">To:</Label>
                  <Input
                    type="date"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    className="w-40"
                  />
                </div>
              </div>
            )}
            
            {/* Results count */}
            {(searchTerm || filterCategory !== 'all' || dateFilter !== 'all') && (
              <p className="text-sm text-gray-500">
                Showing {filteredExpenses.length} of {expenses.length} expenses
                {searchTerm && <span> matching "{searchTerm}"</span>}
                {filterCategory !== 'all' && <span> in {filterCategory}</span>}
              </p>
            )}
          </div>

          {expenses.length === 0 ? (
            <div className="text-center py-12">
              <CreditCard className="h-16 w-16 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500 text-lg">No expenses recorded</p>
              <p className="text-gray-400 text-sm">Add your first expense to start tracking</p>
            </div>
          ) : filteredExpenses.length === 0 ? (
            <div className="text-center py-12">
              <Search className="h-16 w-16 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500 text-lg">No expenses found</p>
              <p className="text-gray-400 text-sm">Try adjusting your search or filters</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b-2 border-gray-200">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Date</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Category</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Description</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Vendor</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Status</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Amount</th>
                    <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredExpenses.map((expense) => (
                    <tr key={expense.id} className="hover:bg-gray-50 transition-colors duration-150">
                      <td className="px-4 py-4 text-sm text-gray-600">{expense.date}</td>
                      <td className="px-4 py-4">
                        <span className={`px-3 py-1 rounded-full text-xs font-semibold ${getCategoryColor(expense.category)}`}>
                          {expense.category}
                        </span>
                      </td>
                      <td className="px-4 py-4 text-sm text-gray-700 max-w-xs truncate">{expense.description}</td>
                      <td className="px-4 py-4 text-sm text-gray-600">{expense.vendor || '-'}</td>
                      <td className="px-4 py-4">
                        {expense.status === 'unpaid' ? (
                          <div className="flex flex-col gap-1">
                            <span className="inline-block w-fit px-2 py-0.5 rounded-full text-[11px] font-semibold bg-amber-100 text-amber-800" data-testid={`expense-status-${expense.id}`}>Unpaid{expense.due_date ? ` · due ${expense.due_date}` : ''}</span>
                            <button type="button" onClick={() => handleMarkPaid(expense)} className="text-[11px] text-blue-600 hover:underline text-left" data-testid={`mark-paid-${expense.id}`}>Mark paid</button>
                          </div>
                        ) : (
                          <span className="inline-block px-2 py-0.5 rounded-full text-[11px] font-semibold bg-green-100 text-green-700" data-testid={`expense-status-${expense.id}`}>Paid</span>
                        )}
                      </td>
                      <td className="px-4 py-4 text-sm font-semibold text-red-600">{formatMoney(expense.amount, businessCurrency)}</td>
                      <td className="px-4 py-4 text-right">
                        <div className="flex justify-end gap-2">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleEditClick(expense)}
                            className="hover:bg-blue-50 text-blue-600"
                            title="Edit Expense"
                            data-testid={`edit-expense-${expense.id}`}
                          >
                            <Edit className="h-4 w-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleDeleteExpense(expense.id)}
                            className="hover:bg-red-50 text-red-600"
                            title="Delete Expense"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Edit Expense Dialog */}
      <Dialog open={isEditDialogOpen} onOpenChange={(open) => {
        setIsEditDialogOpen(open);
        if (!open) {
          setEditingExpense(null);
          resetForm();
        }
      }}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Edit Expense</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleUpdateExpense} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="edit_category">Category *</Label>
                <Select value={formData.category} onValueChange={(value) => setFormData(prev => ({ ...prev, category: value }))}>
                  <SelectTrigger id="edit_category">
                    <SelectValue placeholder="Select category" />
                  </SelectTrigger>
                  <SelectContent>
                    {allCategories.map(category => (
                      <SelectItem key={category} value={category}>{category}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="edit_amount">Amount ($) *</Label>
                <Input
                  id="edit_amount"
                  name="amount"
                  type="number"
                  step="0.01"
                  value={formData.amount}
                  onChange={handleInputChange}
                  required
                  placeholder="0.00"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="edit_date">Date *</Label>
                <Input
                  id="edit_date"
                  name="date"
                  type="date"
                  value={formData.date}
                  onChange={handleInputChange}
                  required
                />
              </div>
              <div>
                <Label htmlFor="edit_vendor">Vendor</Label>
                <Input
                  id="edit_vendor"
                  name="vendor"
                  value={formData.vendor}
                  onChange={handleInputChange}
                  placeholder="Enter vendor name"
                />
              </div>
            </div>
            {renderPaymentStatusFields("edit_")}
            <div>
              <Label htmlFor="edit_description">Description *</Label>
              <Textarea
                id="edit_description"
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                required
                placeholder="Enter expense description"
                rows={3}
              />
            </div>
            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={() => {
                setIsEditDialogOpen(false);
                setEditingExpense(null);
                resetForm();
              }}>
                Cancel
              </Button>
              <Button type="submit" className="bg-blue-600 hover:bg-blue-700">
                Update Expense
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Expenses;
